import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SubmitHandler } from 'react-hook-form';
import { useNotify } from '@hotjar/hotsauce';
import { useCurrentSite, sendTrackingCodeByEmail } from '@hotjar/site';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { navigateTo } from '@hotjar/webapp/utils/routing';
import { OVERVIEW } from '@hotjar/webapp/constants/routes';
import type { TrackingSite } from '@hotjar/webapp/tracking/utils';

export const UI_POSITION = 'Hotjar Installer - send email';

export const UI_POSITION_MOBILE = 'Hotjar Installer - send email mobile';

const FORM_NAME = 'Send tracking code by email';

export interface OnSubmitData {
  recipientEmail: string;
}

interface UseEmailTrackingCode {
  onContinueToOverview: () => void;
  onSubmit: SubmitHandler<OnSubmitData>;
  isProcessing: boolean;
  isEmailSent: boolean;
  lastRecipientEmail: string;
}

export const useEmailTrackingCode = (isMobile = false): UseEmailTrackingCode => {
  const { data: currentSite } = useCurrentSite();
  const track = useTrack();
  const { t, i18n } = useTranslation();
  const currentLocale = i18n.languages?.[0];
  const { showToast } = useNotify();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [lastRecipientEmail, setLastRecipientEmail] = useState('');

  const onContinueToOverview = () => {
    if (currentSite?.id) {
      navigateTo(OVERVIEW.BASE.path, { siteId: currentSite?.id });
      track.click(
        'Continue',
        isMobile ? UI_POSITION_MOBILE : UI_POSITION,
        track.getGroupDataForSite(currentSite),
      );
    }
  };

  const onSubmit = async ({ recipientEmail }: OnSubmitData) => {
    const accountId = currentSite?.account?.id;
    const siteId = currentSite?.id;

    setIsProcessing(true);

    if (accountId && siteId) {
      const { success, errorMessage } = await sendTrackingCodeByEmail(accountId, siteId, {
        recipientEmail,
        locale: currentLocale,
      });
      if (success) {
        setLastRecipientEmail(recipientEmail);
        setIsEmailSent(true);
        if (!isMobile) {
          showToast({
            text: t('activation:tracking-code.installation.send-email.success-toast'),
            kind: 'success',
          });
        }
      } else {
        showToast({
          text: errorMessage || t('activation:tracking-code.installation.send-email.error-toast'),
          kind: 'danger',
        });
      }
      track.formSubmit(
        FORM_NAME,
        success,
        track.getGroupDataForSite(currentSite as TrackingSite),
        {},
      );
    }

    setIsProcessing(false);
  };

  return {
    onContinueToOverview,
    onSubmit,
    isProcessing,
    isEmailSent,
    lastRecipientEmail,
  };
};
